import { type ChangeEvent, useRef, useState } from "react"
import { useFormContext } from "react-hook-form"
import { LuImage, LuLoaderCircle, LuUpload } from "react-icons/lu"

import { uploadThumbnail } from "~/v2/api/uploads"
import Button from "~/v2/components/ui/button"
import useThumbnail from "~/v2/hooks/use-thumbnail"
import type {
  VaasCreateValues,
  VaasEditValues,
  VaasThumbnail,
} from "~/v2/types/vaas"

const uploadPreset = import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
type VaasThumbnailFieldProps = {
  description?: string
  disabled?: boolean
  onUploaded?: (thumbnail: VaasThumbnail) => Promise<void> | void
}
type VaasFormValues = VaasCreateValues | VaasEditValues

const VaasThumbnailField = ({
  description = "Primary image shown for this VAAS item.",
  disabled = false,
  onUploaded,
}: VaasThumbnailFieldProps) => {
  const { setValue, watch } = useFormContext<VaasFormValues>()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [uploading, setUploading] = useState(false)
  const thumbnail = watch("course_thumbnail")
  const thumbnailUrl = useThumbnail(thumbnail, 1280)

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ""

    if (!file) {
      return
    }

    setUploading(true)

    try {
      const uploadResponse = await uploadThumbnail(file, uploadPreset)
      const nextThumbnail = {
        ...uploadResponse.data,
        original_filename: file.name,
        originial_filename: file.name,
      } as VaasThumbnail

      setValue("course_thumbnail", nextThumbnail as any, { shouldDirty: true })

      await onUploaded?.(nextThumbnail)
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="flex max-w-md flex-col gap-3 md:col-span-2">
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-foreground">
          Course thumbnail
        </span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </div>

      <div className="overflow-hidden rounded-md border border-dashed border-border bg-muted/30">
        {thumbnailUrl ? (
          <img
            alt="Course thumbnail preview"
            className="aspect-video w-full object-cover"
            src={thumbnailUrl}
          />
        ) : (
          <div className="flex aspect-video w-full items-center justify-center bg-muted/50 text-muted-foreground">
            <div className="flex flex-col items-center gap-2 text-center">
              <LuImage className="h-8 w-8" />
              <span className="text-sm">No thumbnail uploaded</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          accept="image/*"
          className="hidden"
          onChange={handleChange}
          ref={inputRef}
          type="file"
        />
        <Button
          disabled={uploading || disabled}
          onClick={() => inputRef.current?.click()}
          type="button"
          variant="outline"
        >
          {uploading ? (
            <>
              <LuLoaderCircle className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <LuUpload className="mr-2 h-4 w-4" />
              {thumbnailUrl ? "Replace thumbnail" : "Upload thumbnail"}
            </>
          )}
        </Button>
        {thumbnail?.original_filename || thumbnail?.originial_filename ? (
          <span className="text-sm text-muted-foreground">
            {thumbnail.original_filename || thumbnail.originial_filename}
          </span>
        ) : null}
      </div>
    </div>
  )
}

export default VaasThumbnailField
